/**
 * Course card component
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { Course } from '../../types/models';
import './course-card.css';

type CourseCardProps = Readonly<{
  course: Course;
  detailBasePath?: '/courses' | '/my-courses';
}>;

/**
 * Pure function to format course level
 */
const formatLevel = (level: string): string => {
  return level
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const formatDuration = (minutes: number): string => {
  if (minutes <= 0) return '0h';
  return `${Math.max(1, Math.round(minutes / 60))}h`;
};

const formatStudentCount = (students: number): string => {
  if (students >= 1000) {
    return `${(students / 1000).toFixed(1)}k`;
  }
  return `${students}`;
};

/**
 * Course card component
 */
export const CourseCard: React.FC<CourseCardProps> = ({ course, detailBasePath = '/courses' }) => {
  const navigate = useNavigate();
  
  const handleClick = () => {
    navigate(`${detailBasePath}/${encodeURIComponent(course.id)}`);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };
  
  return (
    <article
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className="course-card group"
      aria-label={`Open ${course.title}`}
    >
      <div className="course-card-shine" />

      <div className="course-card-media relative aspect-video overflow-hidden">
        <img
          src={course.thumbnail}
          alt={course.title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute left-3 top-3 flex gap-2">
          <span className="course-tag rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-100">
            {formatLevel(course.level)}
          </span>
          <span className="course-tag rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-100">
            {course.language}
          </span>
        </div>
        <div className="absolute bottom-3 right-3 rounded-full border border-amber-200/60 bg-amber-200/20 px-3 py-1 text-xs font-semibold text-amber-100 backdrop-blur-sm">
          ★ {course.rating.toFixed(1)}
        </div>
      </div>
      
      <div className="p-5">
        <div className="mb-3 flex items-center">
          <img
            src={course.instructor.avatar}
            alt={course.instructor.name}
            className="mr-2 h-9 w-9 rounded-full border border-white/45 object-cover"
            loading="lazy"
          />
          <div>
            <p className="text-sm font-semibold text-slate-100">{course.instructor.name}</p>
            <p className="text-xs text-slate-300/90">{course.instructor.title}</p>
          </div>
        </div>
        
        <h3 className="mb-2 text-xl font-semibold text-white line-clamp-2">
          {course.title}
        </h3>
        
        <p className="mb-4 text-sm text-slate-300/90 line-clamp-2">
          {course.shortDescription}
        </p>

        <div className="mb-4 flex flex-wrap gap-2">
          {course.topics.slice(0, 3).map((topic) => (
            <span
              key={topic}
              className="rounded-full border border-slate-500/45 bg-slate-900/50 px-3 py-1 text-[11px] font-medium text-slate-100"
            >
              {topic}
            </span>
          ))}
        </div>

        <div className="rounded-xl border border-slate-500/30 bg-slate-950/35 p-3">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-3 text-slate-300">
              <span>{course.lessonsCount} lessons</span>
              <span>{formatDuration(course.duration)}</span>
              <span>{formatStudentCount(course.studentsCount)} students</span>
            </div>
            <div className="flex items-center text-slate-100">
              <span>{course.reviewsCount} reviews</span>
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <span className="text-xs uppercase tracking-[0.17em] text-amber-100/90">
              View Course Details
            </span>
            <span className="text-lg text-amber-200 transition-transform duration-300 group-hover:translate-x-1">
              →
            </span>
          </div>
        </div>
      </div>
    </article>
  );
};
